import { CommonModule, Location } from '@angular/common';
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, Router, RouterOutlet } from '@angular/router';
import {
  MsalBroadcastService,
  MsalModule,
  MsalService,
} from '@azure/msal-angular';
import {
  EventMessage,
  EventType,
  InteractionStatus,
} from '@azure/msal-browser';
import { initFlowbite } from 'flowbite';
import { Subject, takeUntil } from 'rxjs';
import { NavbarComponent } from './common/components/navbar/navbar.component';
import { SpinnerComponent } from './common/components/spinner/spinner.component';
import { SpinnerState } from './common/state-stores/spinner.state';
import { PlatformheaderComponent } from './common/components/platformheader/platformheader/platformheader.component';
import { AppScrollableState } from './common/state-stores/app-scrollable.state';
import { AppUpdateService } from './common/services/app-update-service/app-update.service';
import { AppUpdateState } from './common/state-stores/app-update.state';
import { AppDataState } from './common/state-stores/app-data-state.state';
import { environment } from '../environments/environment';
import { BackgroundOperationResultModalComponent } from './common/components/background-operation-result-modal/background-operation-result-modal.component';
import { BackgroundOperationPendingToastComponent } from './common/components/background-operation-pending-toast/background-operation-pending-toast.component';
import { TranslocoModule } from '@jsverse/transloco';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    MsalModule,
    NavbarComponent,
    CommonModule,
    TranslocoModule,
    SpinnerComponent,
    PlatformheaderComponent,
    BackgroundOperationResultModalComponent,
    BackgroundOperationPendingToastComponent,
  ],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'WaterMain';
  private readonly _destroying$ = new Subject<void>();
  loginDisplay = false;
  showDetailForm = false; // or set this based on your logic
  isNavbarCollapsed = false;
  isMfaRoute = false;
  /** True for the whole duration of the post-login MFA status check + resulting navigation, so
   * the header/navbar stay hidden and the spinner stays visible instead of flashing the bare
   * login/landing page while this async work is in flight. */
  isResolvingPostLoginRoute = false;

  reloadApp() {
    this.appUpdateState.hideUpdateBanner(); // Hide the banner after reload
    window.location.reload();
  }

  constructor(
    private readonly authService: MsalService,
    private readonly msalBroadcastService: MsalBroadcastService,
    private readonly router: Router,
    private readonly appState: AppDataState,
    protected readonly spinnerState: SpinnerState,
    private readonly location: Location,
    protected readonly appScrollableState: AppScrollableState,
    private appUpdateService: AppUpdateService,
    public appUpdateState: AppUpdateState,
  ) {}

  onNavbarToggled(isCollapsed: boolean) {
    this.isNavbarCollapsed = isCollapsed;
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  /**
   * Shared handling for MSAL LOGIN_SUCCESS and inProgress$ === None: decides whether to land on
   * /login or the platform home page. Our own (non-Entra) MFA is no longer checked here -
   * `mfaGuard` (applied to every protected route in app.routes.ts) handles that uniformly via
   * `canActivate`, which blocks the destination component from ever being instantiated while
   * MFA is outstanding, redirecting to /mfa instead. Checking it here too (after the route has
   * already started activating) was both redundant and let the destination page's content flash
   * on screen for a moment before the redirect landed.
   *
   * The only behavioral difference between the two call sites is whether a deep-linked
   * "portal/:operatorName" URL should still redirect to /login when there's no account -
   * LOGIN_SUCCESS always does; inProgress$ None does not, so as not to bounce an
   * operator-landing visitor who was never meant to be authenticated here.
   *
   * The spinner is kept visible and the header/navbar kept hidden for the whole duration of the
   * resulting navigation (see isResolvingPostLoginRoute), so the user never sees a flash of the
   * bare login/landing page with no spinner while that async work runs.
   */
  private async handlePostAuthNavigation(options: { navigateToLoginEvenOnPortalPath: boolean }): Promise<void> {
    this.checkAndSetActiveAccount();
    this.setLoginDisplay();

    if (this.location.path().includes('apply')) {
      this.loginDisplay = false;
      this.spinnerState.hideSpinner();
      sessionStorage.removeItem('lastSpinnerState');
      return;
    }

    if (this.authService.instance.getAllAccounts().length === 0) {
      if (options.navigateToLoginEvenOnPortalPath || !this.location.path().includes('portal')) {
        this.router.navigate(['/login']);
      }
    } else if (
      this.location.path() === '/' ||
      this.location.path() === '/login' ||
      this.location.path() === '' ||
      this.location.path().includes('portal')
    ) {
      this.isResolvingPostLoginRoute = true;
      this.spinnerState.showSpinner();
      try {
        // mfaGuard on 'platform/home' decides whether to actually land there or get redirected
        // to /mfa - nothing more to resolve here.
        await this.router.navigate(['platform/home']);
      } finally {
        this.isResolvingPostLoginRoute = false;
      }
    }
    // Else: authenticated and already sitting on some other route (deep link, refresh, etc.) -
    // mfaGuard already gated entry to it before this component was ever allowed to activate.

    this.spinnerState.hideSpinner();
    sessionStorage.removeItem('lastSpinnerState');
  }

  checkAndSetActiveAccount() {
    let activeAccount = this.authService.instance.getActiveAccount();

    if (
      !activeAccount &&
      this.authService.instance.getAllAccounts().length > 0
    ) {
      let accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  private scheduleFlowbiteInit(): void {
    const runInit = () => initFlowbite();
    const requestIdle = (window as Window & {
      requestIdleCallback?: (cb: () => void) => number;
    }).requestIdleCallback;

    if (requestIdle) {
      requestIdle(runInit);
      return;
    }

    setTimeout(runInit, 0);
  }

  async ngOnInit(): Promise<void> {
    this.appState.setCurrencyCode(environment.currencyCode ?? 'CAD');

    this.appUpdateService.startCheckingForUpdates();

    // Lock screen orientation to portrait on mobile devices
    this.lockOrientationToPortrait();

    this.scheduleFlowbiteInit();

    this.isMfaRoute = this.location.path().toLowerCase().startsWith('/mfa');

    if (!this.location.path().includes('stationapp')) {
      this.appState.setStationAppConfigPermissionPresent(false);
    }


    if (sessionStorage.getItem('lastSpinnerState') === 'true') {
      this.spinnerState.showSpinner();
    } else {
      this.spinnerState.hideSpinner();
      sessionStorage.removeItem('lastSpinnerState');
    }

    this.authService.handleRedirectObservable().subscribe();
    this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window

    this.msalBroadcastService.msalSubject$
      .pipe(takeUntil(this._destroying$))
      .subscribe((result: EventMessage) => {
        if (result.eventType === EventType.LOGIN_SUCCESS) {
          void this.handlePostAuthNavigation({ navigateToLoginEvenOnPortalPath: true });
        } else if (result.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
          sessionStorage.clear();
          this.loginDisplay = false;
          this.router.navigate(['/login']);
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(takeUntil(this._destroying$))
      .subscribe((status: InteractionStatus) => {
        if (status === InteractionStatus.Login) {
          this.spinnerState.showSpinner();
          sessionStorage.setItem(
            'lastSpinnerState',
            this.spinnerState.loading().toString()
          );
        } else if (status === InteractionStatus.None) {
          void this.handlePostAuthNavigation({ navigateToLoginEvenOnPortalPath: false });
        }
      });

    this.router.events
      .pipe(takeUntil(this._destroying$))
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.isMfaRoute = event.urlAfterRedirects.toLowerCase().startsWith('/mfa');
          console.log(`[MFA] NavigationEnd to "${event.urlAfterRedirects}" - isMfaRoute=${this.isMfaRoute}, loginDisplay=${this.loginDisplay}`);
        }

        if (!this.spinnerState.isAppLaunchSpinnerActive()) {
          return;
        }

        if (event instanceof NavigationEnd) {
          if (this.isAppHomeUrl(event.urlAfterRedirects.toLowerCase())) {
            this.spinnerState.hideAppLaunchSpinner();
          }
          return;
        }

        if (event instanceof NavigationCancel || event instanceof NavigationError) {
          this.spinnerState.hideAppLaunchSpinner();
        }
      });
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  private lockOrientationToPortrait(): void {
    // Check if the device is mobile and if the Screen Orientation API is available
    if (this.isMobileDevice() && 'orientation' in screen && 'lock' in screen.orientation) {
      const orientation = screen.orientation as any;
      orientation.lock('portrait').catch((error: any) => {
        console.warn('Screen orientation lock failed:', error);
      });
    }
  }

  private isMobileDevice(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  private isAppHomeUrl(url: string): boolean {
    return /^\/[^/]+\/home(?:$|[/?#])/.test(url) && !url.startsWith('/platform/home');
  }

  onRouteActivate(container: HTMLElement): void {
    container.scrollTop = 0;
    const page = container.querySelector(':scope > router-outlet + *');
    if (!page) {
      return;
    }
    page.classList.remove('route-enter');
    // Force reflow so the animation restarts when navigating between pages.
    void (page as HTMLElement).offsetWidth;
    page.classList.add('route-enter');
    page.addEventListener('animationend', () => page.classList.remove('route-enter'), { once: true });
  }
}
